import React, { forwardRef, useImperativeHandle, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useIntl } from "react-intl";
import { Button, Input } from "../../../../BP2Components";
import { ActionConfig, ActionParamMeta, ActionsMetaResponse } from "../../ListBuildertypes";

function validateParamValue(
   value: string,
   meta: ActionParamMeta,
   requiredMessage: string,
): string | null {
   const trimmed = value?.trim() ?? "";

   if (meta.mandatory && !trimmed) return requiredMessage;

   if (trimmed && meta.type === "map<string,string>") {
      let parsed: unknown;
      try {
         parsed = JSON.parse(trimmed);
      } catch {
         return "Nieprawidłowy format JSON";
      }
      if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
         return 'Wartość musi być obiektem JSON, np. {"kolumna": "fieldId"}';
      }
      if (Object.values(parsed as Record<string, unknown>).some((v) => typeof v !== "string")) {
         return "Wszystkie wartości w obiekcie JSON muszą być ciągami znaków";
      }
   }

   return null;
}

interface ActionsBuilderProps {
   actions: ActionConfig[];
   onChange: (actions: ActionConfig[]) => void;
   actionsMeta: ActionsMetaResponse;
}

export interface ActionsBuilderHandle {
   validate: () => boolean;
}

const ActionsBuilder = forwardRef<ActionsBuilderHandle, ActionsBuilderProps>(
   ({ actions, onChange, actionsMeta }, ref) => {
      const intl = useIntl();
      const actionNames = Object.keys(actionsMeta);
      const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

      useImperativeHandle(
         ref,
         () => ({
            validate: () => {
               const errors: Record<string, string> = {};
               actions.forEach((action) => {
                  if (!action.actionName) {
                     errors[`${action.id}__actionName`] = intl.formatMessage({
                        id: "field.required",
                     });
                     return;
                  }
                  const params = actionsMeta[action.actionName];
                  if (!params) return;
                  const requiredMessage = intl.formatMessage({ id: "field.required" });
                  Object.entries(params).forEach(([paramName, meta]) => {
                     const error = validateParamValue(
                        action.args[paramName] ?? "",
                        meta,
                        requiredMessage,
                     );
                     if (error) errors[`${action.id}__${paramName}`] = error;
                  });
               });
               setValidationErrors(errors);
               return Object.keys(errors).length === 0;
            },
         }),
         [actions, actionsMeta],
      );

      const clearErrorsForAction = (id: string) => {
         setValidationErrors((prev) => {
            const next = { ...prev };
            Object.keys(next).forEach((key) => {
               if (key.startsWith(`${id}__`)) delete next[key];
            });
            return next;
         });
      };

      const addAction = () => {
         onChange([...actions, { id: uuidv4(), name: "", actionName: "", args: {} }]);
      };

      const removeAction = (id: string) => {
         onChange(actions.filter((a) => a.id !== id));
         clearErrorsForAction(id);
      };

      const updateAction = (id: string, patch: Partial<ActionConfig>) => {
         onChange(actions.map((a) => (a.id === id ? { ...a, ...patch } : a)));
      };

      const handleActionNameChange = (id: string, actionName: string) => {
         // Reset args when action type changes — old args are incompatible with the new action's param schema
         updateAction(id, { actionName, args: {} });
         clearErrorsForAction(id);
      };

      const handleArgChange = (
         id: string,
         action: ActionConfig,
         paramName: string,
         value: string,
      ) => {
         updateAction(id, { args: { ...action.args, [paramName]: value } });
         const errorKey = `${id}__${paramName}`;
         setValidationErrors((prev) => {
            if (!prev[errorKey]) return prev;
            const next = { ...prev };
            delete next[errorKey];
            return next;
         });
      };

      return (
         <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {actions.map((action, index) => {
               const params = action.actionName ? actionsMeta[action.actionName] : null;
               const actionNameError = validationErrors[`${action.id}__actionName`];

               return (
                  <div
                     key={action.id}
                     style={{
                        border: "1px solid #d9d9d9",
                        borderRadius: "4px",
                        padding: "0.75rem",
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.5rem",
                     }}
                  >
                     <div
                        style={{
                           display: "flex",
                           justifyContent: "space-between",
                           alignItems: "center",
                        }}
                     >
                        <span style={{ fontWeight: 500, fontSize: "0.85rem" }}>
                           Akcja #{index + 1}
                        </span>
                        <Button buttonType="secondary" onClick={() => removeAction(action.id)}>
                           Usuń
                        </Button>
                     </div>

                     <div>
                        <label>Nazwa wyświetlana:</label>
                        <Input
                           value={action.name}
                           onChange={(e) => updateAction(action.id, { name: e.target.value })}
                           placeholder="Wpisz nazwę akcji"
                        />
                     </div>

                     <div>
                        <label>Typ akcji:</label>
                        <select
                           value={action.actionName}
                           onChange={(e) => handleActionNameChange(action.id, e.target.value)}
                           style={{
                              width: "100%",
                              padding: "0.4rem",
                              border: `1px solid ${actionNameError ? "red" : "#d9d9d9"}`,
                              borderRadius: "4px",
                           }}
                        >
                           <option value="">-- wybierz --</option>
                           {actionNames.map((name) => (
                              <option key={name} value={name}>
                                 {name}
                              </option>
                           ))}
                        </select>
                        {actionNameError && (
                           <span
                              style={{
                                 color: "red",
                                 fontSize: "0.78rem",
                                 marginTop: "2px",
                                 display: "block",
                              }}
                           >
                              {actionNameError}
                           </span>
                        )}
                     </div>

                     {params && Object.keys(params).length > 0 && (
                        <div
                           style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "0.4rem",
                              paddingLeft: "0.5rem",
                              borderLeft: "2px solid #e8e8e8",
                           }}
                        >
                           {Object.entries(params).map(
                              ([paramName, meta]: [string, ActionParamMeta]) => {
                                 const errorKey = `${action.id}__${paramName}`;
                                 const errorMessage = validationErrors[errorKey];
                                 const hasError = !!errorMessage;

                                 return (
                                    <div key={paramName}>
                                       <label style={{ fontSize: "0.85rem" }}>
                                          {meta.mandatory && (
                                             <span style={{ color: "red" }}>*</span>
                                          )}{" "}
                                          {paramName}
                                       </label>
                                       {meta.type === "map<string,string>" ? (
                                          <textarea
                                             value={action.args[paramName] ?? ""}
                                             onChange={(e) =>
                                                handleArgChange(
                                                   action.id,
                                                   action,
                                                   paramName,
                                                   e.target.value,
                                                )
                                             }
                                             placeholder={'{"kolumna": "fieldId"}'}
                                             rows={3}
                                             style={{
                                                width: "100%",
                                                padding: "0.4rem",
                                                border: `1px solid ${hasError ? "red" : "#d9d9d9"}`,
                                                borderRadius: "4px",
                                                fontFamily: "monospace",
                                                fontSize: "0.8rem",
                                                boxSizing: "border-box",
                                             }}
                                          />
                                       ) : (
                                          <Input
                                             value={action.args[paramName] ?? ""}
                                             onChange={(e) =>
                                                handleArgChange(
                                                   action.id,
                                                   action,
                                                   paramName,
                                                   e.target.value,
                                                )
                                             }
                                             placeholder={`Wpisz ${paramName}`}
                                             style={{
                                                border: hasError ? "1px solid red" : undefined,
                                             }}
                                          />
                                       )}
                                       {hasError && (
                                          <span
                                             style={{
                                                color: "red",
                                                fontSize: "0.78rem",
                                                marginTop: "2px",
                                                display: "block",
                                             }}
                                          >
                                             {errorMessage}
                                          </span>
                                       )}
                                    </div>
                                 );
                              },
                           )}
                        </div>
                     )}
                  </div>
               );
            })}

            <Button buttonType="secondary" onClick={addAction}>
               + Dodaj akcję
            </Button>
         </div>
      );
   },
);

ActionsBuilder.displayName = "ActionsBuilder";

export default ActionsBuilder;
